import { useState, useRef, useEffect, useCallback } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

/* ── KaTeX renderer — exported for use across the app ────────────────────── */
export function renderMath(text) {
  if (!text) return null;
  const parts = text.split(/(\$[^$]+\$)/g);
  return parts.map((part, i) => {
    if (part.startsWith('$') && part.endsWith('$') && part.length > 2) {
      const latex = part.slice(1, -1);
      try {
        const html = katex.renderToString(latex, { throwOnError: false, displayMode: false });
        return <span key={i} dangerouslySetInnerHTML={{ __html: html }} style={{ verticalAlign: 'middle' }} />;
      } catch {
        return <span key={i} style={{ color: 'var(--err)', fontFamily: 'monospace', fontSize: 12 }}>{part}</span>;
      }
    }
    return <span key={i}>{part}</span>;
  });
}
export function MathText({ text, style }) {
  return <span style={style}>{renderMath(text)}</span>;
}

/* ── Keyboard layout definitions ─────────────────────────────────────────── */

// type: 'num' default | 'fn' function key (orange) | 'del' delete (red) | 'nav' tab/space (blue)
const NUM_KB = [
  [
    { d:'7', s:'7' }, { d:'8', s:'8' }, { d:'9', s:'9' },
    { d:'÷', s:'\\div', t:'fn' }, { d:'(', s:'\\left(' }, { d:')', s:')', t:'fn' },
    { d:'⌫', s:'⌫', t:'del', w:1 },
  ],
  [
    { d:'4', s:'4' }, { d:'5', s:'5' }, { d:'6', s:'6' },
    { d:'×', s:'\\times', t:'fn' }, { d:'xⁿ', s:'#@^{#?}', t:'fn' },
    { d:'xₙ', s:'#@_{#?}', t:'fn' }, { d:'Tab→', s:'⇥', t:'nav', w:1 },
  ],
  [
    { d:'1', s:'1' }, { d:'2', s:'2' }, { d:'3', s:'3' },
    { d:'−', s:'-' }, { d:'+', s:'+' },
    { d:'=', s:'=' }, { d:'±', s:'\\pm', t:'fn' },
  ],
  [
    { d:'0', s:'0', w:1.5 }, { d:'.', s:'.' }, { d:',', s:',' },
    { d:'∞', s:'\\infty', t:'fn' }, { d:'π', s:'\\pi', t:'fn' },
    { d:'e', s:'e' }, { d:'θ', s:'\\theta', t:'fn' },
  ],
];

const FUNC_KB = [
  [
    { d:'a/b', s:'\\frac{#?}{#?}', t:'fn', w:1.5, tip:'Fraction' },
    { d:'√', s:'\\sqrt{#?}', t:'fn', tip:'Square root' },
    { d:'ⁿ√', s:'\\sqrt[#?]{#?}', t:'fn', tip:'nth root' },
    { d:'|x|', s:'\\left|#?\\right|', t:'fn', tip:'Absolute value' },
    { d:'xⁿ', s:'#@^{#?}', t:'fn', tip:'Power' },
    { d:'xₙ', s:'#@_{#?}', t:'fn', tip:'Subscript' },
  ],
  [
    { d:'∫ₐᵇ', s:'\\int_{#?}^{#?}#?\\,d#?', t:'fn', w:1.5, tip:'Definite integral' },
    { d:'∫', s:'\\int #?\\,d#?', t:'fn', tip:'Integral' },
    { d:'d/dx', s:'\\frac{d}{dx}#?', t:'fn', tip:'Derivative' },
    { d:'∂/∂x', s:'\\frac{\\partial}{\\partial #?}#?', t:'fn', tip:'Partial' },
    { d:"f′", s:"#?'", t:'fn', tip:"Prime notation" },
    { d:'ẋ', s:'\\dot{#?}', t:'fn', tip:'Time derivative' },
  ],
  [
    { d:'lim', s:'\\lim_{#? \\to #?}', t:'fn', w:1.5, tip:'Limit' },
    { d:'Σ', s:'\\sum_{#?}^{#?}', t:'fn', tip:'Summation' },
    { d:'∏', s:'\\prod_{#?}^{#?}', t:'fn', tip:'Product' },
    { d:'log_b', s:'\\log_{#?}#?', t:'fn', tip:'Log base b' },
    { d:'ln', s:'\\ln{#?}', t:'fn', tip:'Natural log' },
    { d:'eˣ', s:'e^{#?}', t:'fn', tip:'Exponential' },
  ],
  [
    { d:'(a/b)', s:'\\left(\\frac{#?}{#?}\\right)', t:'fn', w:1.5, tip:'Fraction in parens' },
    { d:'nCk', s:'\\binom{#?}{#?}', t:'fn', tip:'Binomial' },
    { d:'mat', s:'\\begin{pmatrix}#? & #?\\\\#? & #?\\end{pmatrix}', t:'fn', tip:'2×2 matrix' },
    { d:'cases', s:'#? = \\begin{cases}#? & #?\\\\#? & #?\\end{cases}', t:'fn', tip:'Piecewise' },
    { d:'≤', s:'\\leq', t:'fn' }, { d:'≥', s:'\\geq', t:'fn' },
  ],
];

const TRIG_KB = [
  [
    { d:'sin', s:'\\sin(#?)', t:'fn', tip:'Sine' },
    { d:'cos', s:'\\cos(#?)', t:'fn', tip:'Cosine' },
    { d:'tan', s:'\\tan(#?)', t:'fn', tip:'Tangent' },
    { d:'cot', s:'\\cot(#?)', t:'fn', tip:'Cotangent' },
    { d:'sec', s:'\\sec(#?)', t:'fn', tip:'Secant' },
    { d:'csc', s:'\\csc(#?)', t:'fn', tip:'Cosecant' },
  ],
  [
    { d:'sin⁻¹', s:'\\arcsin(#?)', t:'fn', tip:'Inverse sine (arcsin)' },
    { d:'cos⁻¹', s:'\\arccos(#?)', t:'fn', tip:'Inverse cosine (arccos)' },
    { d:'tan⁻¹', s:'\\arctan(#?)', t:'fn', tip:'Inverse tangent (arctan)' },
    { d:'cot⁻¹', s:'\\text{arccot}(#?)', t:'fn', tip:'Inverse cotangent' },
    { d:'sec⁻¹', s:'\\text{arcsec}(#?)', t:'fn', tip:'Inverse secant' },
    { d:'csc⁻¹', s:'\\text{arccsc}(#?)', t:'fn', tip:'Inverse cosecant' },
  ],
  [
    { d:'sinh', s:'\\sinh(#?)', t:'fn', tip:'Hyperbolic sine' },
    { d:'cosh', s:'\\cosh(#?)', t:'fn', tip:'Hyperbolic cosine' },
    { d:'tanh', s:'\\tanh(#?)', t:'fn', tip:'Hyperbolic tangent' },
    { d:'coth', s:'\\coth(#?)', t:'fn', tip:'Hyperbolic cotangent' },
    { d:'sin²', s:'\\sin^{2}(#?)', t:'fn', tip:'sin squared' },
    { d:'cos²', s:'\\cos^{2}(#?)', t:'fn', tip:'cos squared' },
  ],
  [
    { d:'°', s:'^{\\circ}', t:'fn', tip:'Degree' },
    { d:'rad', s:'\\text{ rad}', t:'fn', tip:'Radians label' },
    { d:'π/2', s:'\\frac{\\pi}{2}', t:'fn', tip:'Pi over 2 (90°)' },
    { d:'π/3', s:'\\frac{\\pi}{3}', t:'fn', tip:'Pi over 3 (60°)' },
    { d:'π/4', s:'\\frac{\\pi}{4}', t:'fn', tip:'Pi over 4 (45°)' },
    { d:'π/6', s:'\\frac{\\pi}{6}', t:'fn', tip:'Pi over 6 (30°)' },
  ],
];

const GREEK_KB = [
  [
    { d:'α', s:'\\alpha' }, { d:'β', s:'\\beta' }, { d:'γ', s:'\\gamma' },
    { d:'δ', s:'\\delta' }, { d:'ε', s:'\\epsilon' }, { d:'ζ', s:'\\zeta' },
    { d:'η', s:'\\eta' }, { d:'θ', s:'\\theta' },
  ],
  [
    { d:'λ', s:'\\lambda' }, { d:'μ', s:'\\mu' }, { d:'ξ', s:'\\xi' },
    { d:'π', s:'\\pi' }, { d:'ρ', s:'\\rho' }, { d:'σ', s:'\\sigma' },
    { d:'τ', s:'\\tau' }, { d:'φ', s:'\\phi' },
  ],
  [
    { d:'χ', s:'\\chi' }, { d:'ψ', s:'\\psi' }, { d:'ω', s:'\\omega' },
    { d:'Δ', s:'\\Delta' }, { d:'Γ', s:'\\Gamma' }, { d:'Σ', s:'\\Sigma' },
    { d:'Ω', s:'\\Omega' }, { d:'Λ', s:'\\Lambda' },
  ],
  [
    { d:'∈', s:'\\in', t:'fn' }, { d:'∉', s:'\\notin', t:'fn' },
    { d:'⊂', s:'\\subset', t:'fn' }, { d:'⊃', s:'\\supset', t:'fn' },
    { d:'∪', s:'\\cup', t:'fn' }, { d:'∩', s:'\\cap', t:'fn' },
    { d:'∅', s:'\\emptyset', t:'fn' }, { d:'∴', s:'\\therefore', t:'fn' },
  ],
];

// Letter KB: QWERTY rows (lowercase; shifted state toggles case)
const LETTER_ROWS = [
  'q w e r t y u i o p'.split(' '),
  'a s d f g h j k l'.split(' '),
  'z x c v b n m'.split(' '),
];

/* ── Key button component ─────────────────────────────────────────────────── */
function Key({ d, t, w, tip, onPress }) {
  const ref = useRef(null);
  const bg = t === 'fn'  ? 'rgba(255,106,0,0.14)'
           : t === 'del' ? 'rgba(220,38,38,0.18)'
           : t === 'nav' ? 'rgba(96,165,250,0.15)'
           :               'rgba(255,255,255,0.07)';
  const border = t === 'fn'  ? 'rgba(255,106,0,0.3)'
               : t === 'del' ? 'rgba(220,38,38,0.35)'
               : t === 'nav' ? 'rgba(96,165,250,0.3)'
               :               'rgba(255,255,255,0.12)';
  const color = t === 'fn'  ? '#fb923c'
              : t === 'del' ? '#fca5a5'
              : t === 'nav' ? '#93c5fd'
              :               '#e2e8f0';

  return (
    <button
      ref={ref}
      type="button"
      title={tip}
      onPointerDown={e => {
        e.preventDefault();
        if (ref.current) { ref.current.style.transform = 'scale(0.88)'; ref.current.style.opacity = '0.75'; }
        onPress();
      }}
      onPointerUp={() => { if (ref.current) { ref.current.style.transform = ''; ref.current.style.opacity = ''; } }}
      onPointerLeave={() => { if (ref.current) { ref.current.style.transform = ''; ref.current.style.opacity = ''; } }}
      style={{
        flex: w ? `${w} 0 auto` : '1 0 auto',
        minWidth: 0,
        padding: '15px 4px',
        background: bg,
        border: `1px solid ${border}`,
        color,
        borderRadius: 9,
        cursor: 'pointer',
        fontSize: d.length > 3 ? 13 : d.length > 2 ? 15 : 18,
        fontWeight: 600,
        lineHeight: 1.1,
        textAlign: 'center',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        transition: 'transform 0.08s, opacity 0.08s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {d}
    </button>
  );
}

/* ── Equation Editor Modal ────────────────────────────────────────────────── */
function EqModal({ onInsert, onClose }) {
  const mfRef     = useRef(null);
  const [latex,    setLatex]    = useState('');
  const [rendered, setRendered] = useState('');
  const [loaded,   setLoaded]   = useState(false);
  const [kbTab,    setKbTab]    = useState('num');
  const [shifted,  setShifted]  = useState(false);
  const [caps,     setCaps]     = useState(false);
  const [computed, setComputed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    import('mathlive').then(() => {
      if (cancelled) return;
      setLoaded(true);
      requestAnimationFrame(() => {
        const mf = mfRef.current;
        if (!mf) return;
        mf.addEventListener('input', () => {
          const v = mf.value || '';
          setLatex(v);
          try { setRendered(katex.renderToString(v, { throwOnError: false, displayMode: false })); }
          catch { setRendered(''); }
        });
        mf.focus();
      });
    });
    return () => { cancelled = true; };
  }, []);

  const press = useCallback((sym) => {
    const mf = mfRef.current;
    if (!mf) return;
    if (sym === '⌫') { mf.executeCommand('deleteBackward'); }
    else if (sym === '⇥') { mf.executeCommand('moveToNextPlaceholder'); }
    else if (sym.includes('#?') || sym.includes('#@')) {
      mf.executeCommand(['insert', sym, { selectionMode: 'placeholder', format: 'latex' }]);
    } else {
      mf.executeCommand(['insert', sym, { format: 'latex' }]);
    }
    mf.focus();
    setComputed(null);
  }, []);

  const computeResult = useCallback(async () => {
    const mf = mfRef.current;
    if (!mf || !mf.value) return;
    try {
      const { evaluate } = await import('mathjs');
      // Try ascii-math first, fallback to latex with basic cleanup
      let expr = '';
      try { expr = mf.getValue('ascii-math'); } catch { expr = mf.value; }
      const result = evaluate(expr);
      if (typeof result === 'number' || typeof result === 'string') {
        const r = typeof result === 'number' ? (Number.isInteger(result) ? result : parseFloat(result.toFixed(8))) : result;
        setComputed(String(r));
      } else {
        // mathjs object (matrix, unit, etc.)
        setComputed(result.toString());
      }
    } catch {
      setComputed('Cannot evaluate');
    }
  }, []);

  const KB_TABS = [
    { key: 'num',   label: '123',   rows: NUM_KB },
    { key: 'fn',    label: 'f(x)',  rows: FUNC_KB },
    { key: 'trig',  label: 'sin',   rows: TRIG_KB },
    { key: 'greek', label: 'αβγ',   rows: GREEK_KB },
    { key: 'abc',   label: 'abc',   rows: null },
  ];

  const activeRows = kbTab === 'abc' ? null : KB_TABS.find(t => t.key === kbTab)?.rows;

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        style={{ background: '#0f172a', padding: 'env(safe-area-inset-top,0) 0 env(safe-area-inset-bottom,0)', width: '100%', maxWidth: 680, boxShadow: '0 0 48px rgba(0,0,0,0.5)', borderLeft: '1px solid rgba(255,255,255,0.07)', borderRight: '1px solid rgba(255,255,255,0.07)', height: '100%', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px 6px' }}>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>Equation Editor</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {latex && (
              <button type="button" onClick={computeResult}
                style={{ background: 'rgba(96,165,250,0.15)', color: '#93c5fd', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 8, padding: '7px 12px', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
                = Compute
              </button>
            )}
            {latex && (
              <button type="button" onClick={() => { onInsert(`$${latex}$`); }}
                style={{ background: 'var(--org)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                Insert ↵
              </button>
            )}
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: 18, lineHeight: 1, padding: '4px 8px', borderRadius: 6 }}>×</button>
          </div>
        </div>

        {/* Math display field */}
        <div style={{ margin: '6px 12px', background: '#1e293b', borderRadius: 12, padding: '18px 16px', border: '2px solid rgba(255,106,0,0.4)', minHeight: 80, display: 'flex', alignItems: 'center' }}>
          {loaded ? (
            // @ts-ignore
            <math-field ref={mfRef}
              style={{ width: '100%', fontSize: 30, '--caret-color': 'var(--org)', '--selection-background-color': 'rgba(255,106,0,0.25)', background: 'transparent', outline: 'none', color: '#fff' }}
              virtual-keyboard-mode="off"
            />
          ) : (
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>Loading…</span>
          )}
        </div>

        {/* Rendered preview — only when there's something */}
        {rendered && (
          <div style={{ margin: '0 12px 4px', background: '#fff', borderRadius: 8, padding: '8px 14px', textAlign: 'center', minHeight: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span dangerouslySetInnerHTML={{ __html: rendered }} />
          </div>
        )}

        {/* Compute result */}
        {computed && (
          <div style={{ margin: '0 12px 4px', background: computed === 'Cannot evaluate' ? 'rgba(239,68,68,0.1)' : 'rgba(96,165,250,0.1)', borderRadius: 8, padding: '7px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${computed === 'Cannot evaluate' ? 'rgba(239,68,68,0.25)' : 'rgba(96,165,250,0.25)'}` }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: computed === 'Cannot evaluate' ? '#fca5a5' : '#93c5fd' }}>= {computed}</span>
            {computed !== 'Cannot evaluate' && (
              <button type="button" onClick={() => { const mf = mfRef.current; if (mf) { mf.executeCommand(['insert', computed, { format: 'latex' }]); mf.focus(); } setComputed(null); }}
                style={{ background: 'rgba(96,165,250,0.2)', border: 'none', borderRadius: 6, color: '#93c5fd', fontSize: 11, fontWeight: 700, padding: '4px 10px', cursor: 'pointer' }}>
                Insert result
              </button>
            )}
          </div>
        )}

        {/* Keyboard tab selector */}
        <div style={{ display: 'flex', gap: 1, margin: '0 12px 6px', background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: 3 }}>
          {KB_TABS.map(({ key, label }) => (
            <button key={key} type="button" onClick={() => setKbTab(key)}
              style={{ flex: 1, padding: '7px 4px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 800, background: kbTab === key ? 'var(--org)' : 'transparent', color: kbTab === key ? '#fff' : 'rgba(255,255,255,0.4)', transition: 'all 0.15s' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Keyboard grid */}
        <div style={{ padding: '2px 10px 14px', flex: 1, overflowY: 'auto' }}>
          {/* Number / Function / Greek tabs */}
          {activeRows && loaded && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {activeRows.map((row, ri) => (
                <div key={ri} style={{ display: 'flex', gap: 5 }}>
                  {row.map((k) => (
                    <Key key={k.d} d={k.d} t={k.t} w={k.w} tip={k.tip} onPress={() => press(k.s)} />
                  ))}
                </div>
              ))}
            </div>
          )}

          {/* Letter tab — QWERTY style with shift toggle */}
          {kbTab === 'abc' && loaded && (() => {
            const isUpper = shifted || caps;
            const dispRow = (row) => row.map(ch => isUpper ? ch.toUpperCase() : ch);
            const pressLetter = (ch) => {
              press(isUpper ? ch.toUpperCase() : ch);
              // single shift: revert after one key; caps: stay
              if (shifted && !caps) setShifted(false);
            };
            const shiftColor = caps ? 'var(--org)' : shifted ? 'rgba(255,106,0,0.6)' : undefined;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                {/* Rows 1 + 2 */}
                {LETTER_ROWS.slice(0, 2).map((row, ri) => (
                  <div key={ri} style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
                    {dispRow(row).map((ch, ci) => (
                      <Key key={ch} d={ch} onPress={() => pressLetter(LETTER_ROWS[ri][ci])} />
                    ))}
                  </div>
                ))}
                {/* Row 3: ⇧ + letters + ⌫ */}
                <div style={{ display: 'flex', gap: 5, justifyContent: 'center' }}>
                  <button
                    type="button"
                    onPointerDown={e => {
                      e.preventDefault();
                      if (caps) { setCaps(false); setShifted(false); }
                      else if (shifted) { setCaps(true); setShifted(false); }
                      else setShifted(true);
                    }}
                    style={{ flex: '1 0 auto', padding: '10px 4px', background: caps ? 'rgba(255,106,0,0.25)' : shifted ? 'rgba(255,106,0,0.12)' : 'rgba(255,255,255,0.07)', border: `1px solid ${shiftColor || 'rgba(255,255,255,0.12)'}`, color: shiftColor || '#e2e8f0', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 700, userSelect: 'none', WebkitUserSelect: 'none' }}>
                    ⇧
                  </button>
                  {dispRow(LETTER_ROWS[2]).map((ch, ci) => (
                    <Key key={ch} d={ch} onPress={() => pressLetter(LETTER_ROWS[2][ci])} />
                  ))}
                  <Key d="⌫" t="del" onPress={() => press('⌫')} />
                </div>
                {/* Row 4: space + tab */}
                <div style={{ display: 'flex', gap: 5 }}>
                  <Key d="Space" t="nav" w={2} onPress={() => press('\\ ')} />
                  <Key d="Tab→" t="nav" w={1.5} onPress={() => press('⇥')} />
                </div>
              </div>
            );
          })()}

          {/* LaTeX string (dimmed, at bottom) */}
          {latex && (
            <div style={{ marginTop: 10, padding: '5px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, fontSize: 10, fontFamily: 'monospace', color: 'rgba(255,255,255,0.25)', wordBreak: 'break-all' }}>
              {latex}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Quick-symbol panel (outside modal, below textarea) ──────────────────── */
const QUICK = [
  { label: 'Greek', syms: [['α','\\alpha'],['β','\\beta'],['γ','\\gamma'],['δ','\\delta'],['θ','\\theta'],['λ','\\lambda'],['μ','\\mu'],['π','\\pi'],['σ','\\sigma'],['φ','\\phi'],['ω','\\omega'],['Δ','\\Delta'],['Σ','\\Sigma'],['Ω','\\Omega']] },
  { label: 'Ops',   syms: [['±','\\pm'],['×','\\times'],['÷','\\div'],['≠','\\neq'],['≤','\\leq'],['≥','\\geq'],['≈','\\approx'],['∝','\\propto'],['∞','\\infty'],['∴','\\therefore'],['⇒','\\Rightarrow'],['⇔','\\Leftrightarrow'],['∀','\\forall'],['∃','\\exists']] },
  { label: 'Sup',   syms: [['⁰','⁰'],['¹','¹'],['²','²'],['³','³'],['⁴','⁴'],['⁵','⁵'],['⁶','⁶'],['⁷','⁷'],['⁸','⁸'],['⁹','⁹'],['ⁿ','ⁿ']] },
  { label: 'Sub',   syms: [['₀','₀'],['₁','₁'],['₂','₂'],['₃','₃'],['₄','₄'],['₅','₅'],['₆','₆'],['₇','₇'],['₈','₈'],['₉','₉'],['ₙ','ₙ'],['ₓ','ₓ']] },
  { label: 'Sets',  syms: [['∈','∈'],['∉','∉'],['⊂','⊂'],['⊃','⊃'],['∪','∪'],['∩','∩'],['∅','∅'],['ℕ','ℕ'],['ℤ','ℤ'],['ℝ','ℝ'],['ℂ','ℂ']] },
];

/* ── Main export ──────────────────────────────────────────────────────────── */
export default function MathInput({ value = '', onChange, rows = 3, placeholder = '', className = '', style = {}, label, required }) {
  const taRef = useRef(null);
  const [showQuick,   setShowQuick]   = useState(false);
  const [quickTab,    setQuickTab]    = useState(0);
  const [showEq,      setShowEq]      = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const hasMath = value.includes('$');

  const insertAt = useCallback((sym) => {
    const ta = taRef.current;
    if (!ta) { onChange?.(value + sym); return; }
    const s = ta.selectionStart ?? value.length;
    const e = ta.selectionEnd   ?? value.length;
    const next = value.slice(0, s) + sym + value.slice(e);
    onChange?.(next);
    requestAnimationFrame(() => { ta.focus(); const pos = s + sym.length; ta.setSelectionRange(pos, pos); });
  }, [value, onChange]);

  const insertLatex = useCallback((latex) => {
    insertAt(latex);
    setShowEq(false);
    setShowPreview(true);
  }, [insertAt]);

  return (
    <div style={{ width: '100%', ...style }}>
      {label && <label className="form-label" style={{ display: 'block', marginBottom: 4 }}>{label}{required && <span style={{ color: 'var(--err)', marginLeft: 2 }}>*</span>}</label>}

      <textarea ref={taRef} className={`form-input ${className}`} rows={rows}
        placeholder={placeholder || 'Type here — tap "Insert Equation" for fractions, integrals, limits…'}
        value={value} onChange={e => onChange?.(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }} />

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 5, marginTop: 5, flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" onClick={() => { setShowEq(v => !v); setShowQuick(false); }}
          style={{ background: showEq ? 'var(--org)' : '#0f172a', color: showEq ? '#fff' : '#94a3b8', border: `1px solid ${showEq ? 'var(--org)' : 'rgba(255,255,255,0.12)'}`, borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>
          Insert Equation
        </button>
        <button type="button" onClick={() => { setShowQuick(v => !v); setShowEq(false); }}
          style={{ background: showQuick ? 'rgba(255,106,0,0.12)' : 'transparent', color: showQuick ? 'var(--org)' : '#6b7280', border: `1px solid ${showQuick ? 'rgba(255,106,0,0.35)' : 'rgba(0,0,0,0.12)'}`, borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
          α β π…
        </button>
        {hasMath && (
          <button type="button" onClick={() => setShowPreview(v => !v)}
            style={{ background: 'transparent', color: '#6b7280', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 6, padding: '4px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
            {showPreview ? 'Hide preview' : 'Preview math'}
          </button>
        )}
      </div>

      {/* KaTeX preview */}
      {hasMath && showPreview && (
        <div style={{ background: '#fafbfc', border: '1px solid var(--nat-d)', borderRadius: 8, padding: '10px 14px', marginTop: 8, fontSize: 14, lineHeight: 2, color: '#111' }}>
          {renderMath(value)}
        </div>
      )}

      {/* Quick symbol panel */}
      {showQuick && (
        <div style={{ marginTop: 8, background: '#0f172a', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' }}>
            {QUICK.map((cat, i) => (
              <button key={i} type="button" onClick={() => setQuickTab(i)}
                style={{ flex: '0 0 auto', padding: '7px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', color: quickTab === i ? 'var(--org)' : 'rgba(255,255,255,0.35)', borderBottom: quickTab === i ? '2px solid var(--org)' : '2px solid transparent' }}>
                {cat.label}
              </button>
            ))}
          </div>
          <div style={{ padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {QUICK[quickTab].syms.map(([sym, val]) => (
              <button key={sym} type="button" title={val} onClick={() => insertAt(val)}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#e2e8f0', borderRadius: 6, width: 36, height: 34, cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,106,0,0.2)'; e.currentTarget.style.color = 'var(--org)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = '#e2e8f0'; }}>
                {sym}
              </button>
            ))}
          </div>
        </div>
      )}

      {showEq && <EqModal onInsert={insertLatex} onClose={() => setShowEq(false)} />}
    </div>
  );
}
